import ErrorBoundary from 'components/ErrorBoundary';
import { NavigationTracker } from 'components/NavigationTracker';
import App from 'modules/app';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TrekkerProvider } from 'utils/analytics';
import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import {
  applyLocaleToPath,
  extractLocaleFromPath,
  isSupportedLocale,
  stripLocaleFromPath,
} from '~/routing/paths';
import { useDeviceInfoStore } from '~/stores/device';
import { useI18n } from '~/stores/i18n';

const parseLocaleFromPath = (pathname: string) => {
  const locale = extractLocaleFromPath(pathname);
  return isSupportedLocale(locale) ? locale : null;
};

const isSitemap = (p: string) => p.startsWith('/sitemap.xml');

const LocaleLayout: React.FC = () => {
  const { lang: localeFromStore, setLang, userInitiatedSwitch } = useI18n();
  const location = useLocation();
  const accountId = useAuthStore((state) => state.account?.id);
  const accountIsHost = useAuthStore((state) => state.account?.is_host);
  const anonymousDeviceId = useDeviceInfoStore(
    (state) => state.anonymousDeviceId,
  );
  const localeFromPath = parseLocaleFromPath(location.pathname);
  const pathnameWithoutLocale = stripLocaleFromPath(location.pathname);
  const isModalPath = location.state?.modal;
  const navigate = useNavigate();

  useEffect(() => {
    if (isModalPath) return; // do not interfere with modal paths
    if (isCordova()) return; // do not interfere with Cordova navigation
    if (userInitiatedSwitch) return; // do not interfere with user-initiated language changes
    if (isSitemap(location.pathname)) return; // do not interfere with sitemap.xml

    // locale in store, no locale in path -> set language to en and remove locale from path
    if (localeFromStore === 'en' && localeFromPath !== null) {
      setLang('en', 'default').then(() => {
        navigate(
          {
            pathname: pathnameWithoutLocale,
            search: location.search,
            hash: location.hash,
          },
          { replace: true },
        );
      });

      return;
    }

    // locale in path, locale in store, but they differ -> set language from path
    if (localeFromPath && localeFromPath !== localeFromStore) {
      setLang(localeFromPath, 'path').then(() => {
        navigate(
          {
            pathname: applyLocaleToPath(pathnameWithoutLocale, localeFromPath),
            search: location.search,
            hash: location.hash,
          },
          { replace: true },
        );
      });
      return;
    }

    // no locale in path, locale in store (not 'en') -> add locale to path
    if (!localeFromPath && localeFromStore !== 'en') {
      setLang(localeFromStore, 'store').then(() => {
        navigate(
          {
            pathname: applyLocaleToPath(pathnameWithoutLocale, localeFromStore),
            search: location.search,
            hash: location.hash,
          },
          { replace: true },
        );
      });
      return;
    }
  }, [
    isModalPath,
    localeFromPath,
    localeFromStore,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    pathnameWithoutLocale,
    setLang,
    userInitiatedSwitch,
  ]);

  return (
    <ErrorBoundary>
      <TrekkerProvider
        context={{
          path: location.pathname,
          queryString: location.search,
          account: accountId
            ? { id: accountId, is_host: accountIsHost }
            : undefined,
          anonymousDeviceId,
        }}
      >
        <NavigationTracker />
        {/* Leave App in place for now; later you can migrate children under Outlet */}
        <App />
        {/* <Outlet /> */}
      </TrekkerProvider>
    </ErrorBoundary>
  );
};

export default LocaleLayout;
